import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, Loader2, LogOut, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import subscriptionService from '../services/subscriptionService';
import authService from '../services/authService';

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedStates, setSelectedStates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    const user = authService.getCurrentUser();
    if (!user) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }
    setUserData(user);
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [plansData, statesData, statusData] = await Promise.all([
        subscriptionService.getSubscriptionPlans(),
        subscriptionService.getStates(),
        subscriptionService.checkSubscriptionStatus(),
      ]);
      setPlans(plansData);
      setStates(statesData);
      setSubscriptionStatus(statusData);
    } catch (error) {
      toast.error(error || 'Failed to load subscription data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStateToggle = (stateId) => {
    if (!selectedPlan) {
      toast.error('Please select a plan first');
      return;
    }

    const maxStates = selectedPlan.numberOfStates;
    
    if (selectedStates.includes(stateId)) {
      // Remove state
      setSelectedStates(selectedStates.filter(id => id !== stateId));
    } else {
      // Add state if limit not reached
      if (selectedStates.length >= maxStates) {
        toast.error(`Your selected plan allows only ${maxStates} state${maxStates > 1 ? 's' : ''}`);
        return;
      }
      setSelectedStates([...selectedStates, stateId]);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    // Reset selected states if exceeds new plan limit
    if (selectedStates.length > plan.numberOfStates) {
      setSelectedStates(selectedStates.slice(0, plan.numberOfStates));
      toast.info(`State selection adjusted to match plan limit of ${plan.numberOfStates}`);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.error('Please select a subscription plan');
      return;
    }

    if (selectedStates.length === 0) {
      toast.error('Please select at least one state');
      return;
    }

    try {
      setIsPurchasing(true);
      const result = await subscriptionService.purchaseSubscription(
        selectedPlan.subscriptionPlanId,
        selectedStates,
        billingCycle
      );

      if (result.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      } else {
        toast.error('Payment checkout URL not received');
      }
    } catch (error) {
      toast.error(error || 'Failed to initiate subscription');
      setIsPurchasing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen gradient-hero flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-hero py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Top bar with logout */}
        <div className="flex justify-end mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { authService.logout(); navigate('/login'); }}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-display font-bold text-foreground mb-4">
            {subscriptionStatus?.status === 'EXPIRED' ? 'Renew Your Subscription' : 'Welcome, ' + userData?.name + '!'}
          </h1>
          {subscriptionStatus?.status === 'EXPIRED' ? (
            <div className="mb-4">
              <Badge variant="destructive" className="text-base px-4 py-2 mb-3">
                Subscription Expired
              </Badge>
              <p className="text-lg text-destructive">
                Your subscription expired on {new Date(subscriptionStatus.expiresOn).toLocaleDateString()}
              </p>
              <p className="text-muted-foreground mt-2 mb-4">
                Renew now to continue accessing the platform
              </p>
              <p className="text-sm text-muted-foreground">
                Previous Plan: <strong>{subscriptionStatus.planName}</strong>
              </p>
            </div>
          ) : subscriptionStatus?.status === 'INACTIVE' || !subscriptionStatus ? (
            <>
              <p className="text-lg text-muted-foreground mb-2">
                Choose your subscription plan to get started
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Complete your payment to unlock all features
              </p>
            </>
          ) : (
            <p className="text-lg text-muted-foreground mb-2">
              Choose your subscription plan to get started
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Organization: <strong>{userData?.organizationName}</strong>
          </p>
        </div>

        {/* Subscription Plans */}
        <div className="mb-12">
          <h2 className="text-2xl font-display font-semibold text-foreground mb-4 text-center">
            Select Your Plan
          </h2>

          {/* Billing cycle toggle */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-l-full border text-sm font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary'
              }`}
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1.5" />Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-r-full border text-sm font-medium transition-colors ${
                billingCycle === 'yearly'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:border-primary'
              }`}
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1.5" />Yearly
              {billingCycle !== 'yearly' && <span className="ml-1 text-xs text-success">Save more</span>}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const price    = billingCycle === 'yearly' ? plan.priceYearly    : plan.priceMonthly;
              const duration = billingCycle === 'yearly' ? plan.durationInDaysForYearly : plan.durationInDaysForMonthly;
              return (
              <Card
                key={plan.subscriptionPlanId}
                className={`cursor-pointer transition-all hover:shadow-xl ${
                  selectedPlan?.subscriptionPlanId === plan.subscriptionPlanId
                    ? 'border-primary shadow-glow ring-2 ring-primary'
                    : ''
                }`}
                onClick={() => handlePlanSelect(plan)}
              >
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    {selectedPlan?.subscriptionPlanId === plan.subscriptionPlanId && (
                      <Badge className="bg-primary">Selected</Badge>
                    )}
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-foreground">${price}</span>
                    <span className="text-muted-foreground">/{duration} days</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-success mr-2" />
                      <span>{plan.numberOfOperators} Operators</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Check className="w-4 h-4 text-success mr-2" />
                      <span>{plan.numberOfStates} State{plan.numberOfStates > 1 ? 's' : ''}</span>
                    </div>
                    {plan.canViewComplianceReports && (
                      <div className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-success mr-2" />
                        <span>Compliance Reports</span>
                      </div>
                    )}
                    {plan.canDownloadOperatorExcel && (
                      <div className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-success mr-2" />
                        <span>Excel Export</span>
                      </div>
                    )}
                    {plan.canAccessMultiState && (
                      <div className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-success mr-2" />
                        <span>Multi-State Access</span>
                      </div>
                    )}
                    {plan.canAddMultipleUsers && (
                      <div className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-success mr-2" />
                        <span>Multiple Users</span>
                      </div>
                    )}
                    {plan.canUseAuditTrail && (
                      <div className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-success mr-2" />
                        <span>Audit Trail</span>
                      </div>
                    )}
                    {plan.canAccessAIInsights && (
                      <div className="flex items-center text-sm">
                        <Check className="w-4 h-4 text-success mr-2" />
                        <span>AI Insights</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>

        {/* State Selection */}
        {selectedPlan && (
          <div className="mb-8">
            <h2 className="text-2xl font-display font-semibold text-foreground mb-4 text-center">
              Select States ({selectedStates.length}/{selectedPlan.numberOfStates})
            </h2>
            <p className="text-center text-sm text-muted-foreground mb-6">
              You can select up to {selectedPlan.numberOfStates} state{selectedPlan.numberOfStates > 1 ? 's' : ''} with your {selectedPlan.name} plan
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {states.map((state) => (
                <Card
                  key={state.stateId}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedStates.includes(state.stateId)
                      ? 'border-primary bg-primary/5'
                      : ''
                  }`}
                  onClick={() => handleStateToggle(state.stateId)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-foreground">{state.name}</div>
                        <div className="text-sm text-muted-foreground">{state.abbreviation}</div>
                      </div>
                      {selectedStates.includes(state.stateId) && (
                        <Check className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Subscribe Button */}
        <div className="flex justify-center mt-8">
          <Button
            size="lg"
            onClick={handleSubscribe}
            disabled={!selectedPlan || selectedStates.length === 0 || isPurchasing}
            className="min-w-64"
          >
            {isPurchasing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              `Subscribe to ${selectedPlan?.name || 'Plan'} - $${billingCycle === 'yearly' ? (selectedPlan?.priceYearly || 0) : (selectedPlan?.priceMonthly || 0)} (${billingCycle})`
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}